'use client';

import { useEffect } from 'react';

import { sendGAEvent } from '@next/third-parties/google';

import { capturePostHogEvent } from '~/lib/posthog-capture';
import { REDDIT_SIGNUP_COOKIE, trackRedditEvent } from '~/lib/reddit-pixel';

/**
 * Fires browser signup conversions only after the server confirms the account.
 *
 * The server decides *whether* a signup happened and drops the conversion id
 * in a cookie (see `lib/server/reddit-signup-conversion.ts`); this picks it up
 * on whichever page the auth redirect landed on, records the confirmed
 * account in GA4 and PostHog, fires Reddit with the same id so its browser and
 * server copies deduplicate, and clears the cookie so a refresh cannot fire
 * twice.
 *
 * Mounted once in the root layout, because the landing page after signup
 * differs by flow — onboarding, the dashboard, or a team invite.
 */
export function RedditPixelEvents() {
  useEffect(() => {
    const conversionId = readCookie(REDDIT_SIGNUP_COOKIE);

    if (!conversionId) {
      return;
    }

    // Cleared first: if either browser tag is blocked, the confirmed account
    // must not be counted again on every navigation or refresh.
    clearCookie(REDDIT_SIGNUP_COOKIE);

    sendSignupGAEvent();
    capturePostHogEvent('trial_started', {
      conversion_id: conversionId,
      lead_source: 'confirmed_account',
      trial_type: '14_day_pro',
    });
    trackRedditEvent('SignUp', { conversionId });
  }, []);

  return null;
}

type DataLayerWindow = Window & { dataLayer?: object[] };

/**
 * `sendGAEvent` only pushes when `window.dataLayer` already exists, and
 * never creates it. The GA snippet loads `afterInteractive` in a later
 * effect, so a post-signup fire can beat it. Creating the array first
 * queues the event until the real snippet drains it. If the helper still
 * no-ops (its module flag is unset), push the same gtag command ourselves.
 */
function sendSignupGAEvent() {
  if (typeof window === 'undefined') {
    return;
  }

  const w = window as DataLayerWindow;
  w.dataLayer = w.dataLayer ?? [];

  const before = w.dataLayer.length;
  const parameters = {
    lead_source: 'confirmed_account',
    trial_type: '14_day_pro',
  };

  sendGAEvent('event', 'qualify_lead', parameters);

  if (w.dataLayer.length === before) {
    // gtag.js drains IArguments-shaped entries, not plain arrays.
    w.dataLayer.push({
      0: 'event',
      1: 'qualify_lead',
      2: parameters,
      length: 3,
    });
  }
}

function readCookie(name: string) {
  if (typeof document === 'undefined') {
    return null;
  }

  const match = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${name}=`));

  if (!match) {
    return null;
  }

  return decodeURIComponent(match.slice(name.length + 1)) || null;
}

function clearCookie(name: string) {
  document.cookie = `${name}=; Max-Age=0; path=/`;
}
